import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { getASOneDownloadLink } from '@helpers/getASOneDownloadLink';

import { useAPI } from '@providers/useAPI';

import { useQuery } from '@tanstack/react-query';
import { jobsKeys } from 'app/queries/jobs/jobsKeys';
import { useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ClickOpenBelow } from './styles/ClickOpenBelow';
import { CopyRight } from './styles/CopyRight';
import { DontHaveAutoScriptInstalled } from './styles/DontHaveAutoScriptInstalled';
import { DownloadNow } from './styles/DownloadNow';
import { Footer } from './styles/Footer';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { createFetchJobLockedStatusQueryFn } from './api/fetchJobLockedStatus';
import { LaunchButtonWrapper } from './styles/LaunchButtonWrapper';
import { Root } from './styles/Root';
import { TermsPrivacy } from './styles/TermsPrivacy';
import { WaveWrapper } from './styles/WaveWrapper';
import Wave from './wave.svg';

const getLink = (id: string): string => {
  const location = window.location.hostname;
  if (location === 'www.autoscript.ai') {
    return `aso://${id}`;
  }
  if (location === 'demo.autoscript.ai') {
    return `aso-demo://${id}`;
  }
  if (location === 'staging.autoscript.ai') {
    return `aso-dev://${id}`;
  }
  // using staging for local development
  return `aso-dev://${id}`;
};

export const OpenInASOneJobPage = () => {
  const { call } = useAPI();

  const { id } = useParams<{ id: string }>();
  const recordingId = id ?? '';

  const lockedStatusQuery = useQuery({
    queryKey: jobsKeys.lockedStatusByRecordingId(recordingId),
    queryFn: createFetchJobLockedStatusQueryFn(call),
    enabled: recordingId !== '',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const isLoading = lockedStatusQuery.isLoading;
  const notFound = lockedStatusQuery.isError || recordingId === '';
  const isLocked = lockedStatusQuery.data?.result ?? false;
  const shouldAutoLaunch = lockedStatusQuery.isSuccess && !lockedStatusQuery.isFetching && !isLocked;

  const autoLaunchedRecordingId = useRef<string | null>(null);

  const launchHandler = useCallback(() => {
    window.open(new URL(getLink(recordingId)), '_self');
  }, [recordingId]);

  useEffect(() => {
    if (!shouldAutoLaunch || autoLaunchedRecordingId.current === recordingId) {
      return;
    }
    const launchTimeout = setTimeout(() => {
      autoLaunchedRecordingId.current = recordingId;
      launchHandler();
    }, 1000);

    return () => clearTimeout(launchTimeout);
  }, [shouldAutoLaunch, recordingId, launchHandler]);

  if (isLoading) {
    return (
      <Root>
        <AbsoluteSpinner />
      </Root>
    );
  }

  if (notFound) {
    return (
      <Root>
        <ClickOpenBelow>
          <p>
            Sorry, this job was <b>not found</b>.
          </p>
        </ClickOpenBelow>

        <Footer>
          <TermsPrivacy>
            <a href="https://voicescript.ai/privacy-policy/" target="_blank" rel="noreferrer">
              Privacy Policy
            </a>{' '}
            |{' '}
            <a href="https://voicescript.ai/terms-of-service/" target="_blank" rel="noreferrer">
              Terms of Service
            </a>
          </TermsPrivacy>
          <CopyRight>&copy; {new Date().getFullYear()} VoiceScript Inc. All Rights Reserved</CopyRight>
        </Footer>
      </Root>
    );
  }

  if (isLocked) {
    return (
      <Root>
        <ClickOpenBelow>
          <p>
            This job is <b>locked</b>. In order to unlock this job you need to <b>pay remaining sum</b>
          </p>
        </ClickOpenBelow>

        <Footer>
          <TermsPrivacy>
            <a href="https://voicescript.ai/privacy-policy/" target="_blank" rel="noreferrer">
              Privacy Policy
            </a>{' '}
            |{' '}
            <a href="https://voicescript.ai/terms-of-service/" target="_blank" rel="noreferrer">
              Terms of Service
            </a>
          </TermsPrivacy>
          <CopyRight>&copy; {new Date().getFullYear()} VoiceScript Inc. All Rights Reserved</CopyRight>
        </Footer>
      </Root>
    );
  }

  return (
    <Root>
      <ClickOpenBelow>
        <p>
          Click <b>Open AutoScriptOne App</b> on the dialog shown by your browser.
        </p>
        <p>
          If you don’t see a dialog, click <b>Launch App</b> below.
        </p>
      </ClickOpenBelow>
      <LaunchButtonWrapper>
        <Button className="w-[190px]" onClick={launchHandler}>
          Launch AutoScriptOne App
        </Button>
      </LaunchButtonWrapper>
      <WaveWrapper>
        <Wave />
      </WaveWrapper>
      <DontHaveAutoScriptInstalled>Don’t have AutoScriptOne App installed?</DontHaveAutoScriptInstalled>
      <DownloadNow href={getASOneDownloadLink()} target="_blank">
        Download Now
      </DownloadNow>

      <Footer>
        <TermsPrivacy>
          <a href="https://voicescript.ai/privacy-policy/" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>{' '}
          |{' '}
          <a href="https://voicescript.ai/terms-of-service/" target="_blank" rel="noreferrer">
            Terms of Service
          </a>
        </TermsPrivacy>
        <CopyRight>&copy; {new Date().getFullYear()} VoiceScript Inc. All Rights Reserved</CopyRight>
      </Footer>
    </Root>
  );
};
